import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchPushConfig } from '../../api/push';
import { selectIsAuthenticated } from '../../features/auth/authSlice';
import {
  dismissPushPrompt,
  enablePushNotifications,
  shouldShowPushPrompt,
} from '../../utils/pushNotifications';

const PushOptInPrompt = () => {
  const { t } = useTranslation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !shouldShowPushPrompt()) {
      setVisible(false);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        const config = await fetchPushConfig();
        if (active && config.configured && shouldShowPushPrompt()) {
          setVisible(true);
        }
      } catch {
        if (active) setVisible(false);
      }
    }, 8_000);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [isAuthenticated]);

  const handleDismiss = () => {
    dismissPushPrompt();
    setVisible(false);
  };

  const handleEnable = async () => {
    setBusy(true);
    setErrorKey('');

    try {
      const result = await enablePushNotifications();

      if (result.status === 'granted') {
        setVisible(false);
      } else if (result.status === 'denied') {
        setErrorKey('notifications.push.denied');
      } else if (result.status === 'server-unconfigured') {
        setErrorKey('notifications.push.serverUnconfigured');
      } else {
        setErrorKey('notifications.push.unsupported');
      }
    } catch {
      setErrorKey('notifications.push.error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Collapse in={visible} unmountOnExit>
      <Box
        sx={{
          bottom: { xs: 84, sm: 24 },
          left: { xs: 16, sm: 'auto' },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
          position: 'fixed',
          right: { xs: 16, sm: 24 },
          zIndex: (theme) => theme.zIndex.snackbar - 1,
        }}
      >
        <Alert
          icon={<NotificationsActiveOutlinedIcon />}
          severity="info"
          action={
            <IconButton
              aria-label={t('notifications.pushPrompt.dismiss')}
              color="inherit"
              onClick={handleDismiss}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            alignItems: 'flex-start',
            boxShadow: 4,
          }}
        >
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="subtitle2">
                {t('notifications.pushPrompt.title')}
              </Typography>
              <Typography variant="body2">
                {t('notifications.pushPrompt.copy')}
              </Typography>
            </Box>
            {errorKey ? <Typography color="error">{t(errorKey)}</Typography> : null}
            <Button
              color="inherit"
              onClick={handleEnable}
              size="small"
              startIcon={
                busy ? (
                  <CircularProgress color="inherit" size={16} />
                ) : (
                  <NotificationsActiveOutlinedIcon />
                )
              }
              sx={{ alignSelf: 'flex-start' }}
              variant="outlined"
            >
              {t('notifications.pushPrompt.enable')}
            </Button>
          </Stack>
        </Alert>
      </Box>
    </Collapse>
  );
};

export default PushOptInPrompt;
